/** Quality tiers from docs/06-graphics-and-art-direction.md. */
export type Tier = 'low' | 'medium' | 'high';

export interface QualitySettings {
  tier: Tier;
  maxPixelRatio: number;
  shadowMapSize: number;
  grassBlades: number;
  ao: boolean;
  temporalAA: boolean;
  bloom: boolean;
  msaa: number;
}

const SETTINGS: Record<Tier, Omit<QualitySettings, 'tier'>> = {
  low: {
    maxPixelRatio: 1,
    shadowMapSize: 1024,
    grassBlades: 12_000,
    ao: false,
    temporalAA: false,
    bloom: true,
    msaa: 4,
  },
  medium: {
    // 1.25 keeps GTAO + TRAA inside an M1 Air's budget (docs/qa/m0-look-test.md).
    maxPixelRatio: 1.25,
    shadowMapSize: 2048,
    grassBlades: 45_000,
    ao: true,
    temporalAA: true,
    bloom: true,
    msaa: 0,
  },
  high: {
    maxPixelRatio: 2,
    shadowMapSize: 2048,
    grassBlades: 90_000,
    ao: true,
    temporalAA: true,
    bloom: true,
    msaa: 0,
  },
};

function parseTier(value: string | null): Tier | null {
  return value === 'low' || value === 'medium' || value === 'high' ? value : null;
}

/**
 * Picks the default tier. An explicit ?tier= always wins. Without WebGPU we run the WebGL2
 * fallback at Low. Small screens or low device memory get Medium.
 */
export function pickTier(params: URLSearchParams): Tier {
  const forced = parseTier(params.get('tier'));
  if (forced) return forced;
  if (!('gpu' in navigator)) return 'low';
  const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 8;
  const smallScreen = Math.min(screen.width, screen.height) < 700;
  if (memory < 4) return 'low';
  if (smallScreen || memory < 8) return 'medium';
  return 'high';
}

export function qualityFor(tier: Tier, params?: URLSearchParams): QualitySettings {
  const q = { tier, ...SETTINGS[tier] };
  if (!params) return q;
  if (params.has('nobloom')) q.bloom = false;
  // Look-dev and perf tuning overrides: ?grass=20000&pr=1.25&ao=0&shadow=1024
  const num = (key: string) => {
    const v = Number(params.get(key));
    return params.has(key) && Number.isFinite(v) ? v : null;
  };
  q.grassBlades = num('grass') ?? q.grassBlades;
  q.maxPixelRatio = num('pr') ?? q.maxPixelRatio;
  q.shadowMapSize = num('shadow') ?? q.shadowMapSize;
  if (params.get('ao') === '0') q.ao = false;
  return q;
}
